import { Component, OnInit, Input, Output, EventEmitter, Inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MessageService } from 'primeng/api';
import { ActivityProviderService } from '../activity-provider.service';
import { ActivityService } from '@app/_components/activity';
import { AlertService } from '@app/_services';
import { first } from 'rxjs/operators';

@Component({
  selector: 'app-activity-provider-form',
  templateUrl: './activity-provider-form.component.html',
  styleUrls: ['./activity-provider-form.component.scss'],
  providers: [MessageService],
  standalone: false,
})
export class ActivityProviderFormComponent implements OnInit {

  @Input()  activityProvider: any;
  @Output() doAdd    = new EventEmitter<any>();
  @Output() doUpdate = new EventEmitter<any>();
  @Output() doDelete = new EventEmitter<any>();

  formTitle: string;
  isNew: boolean;
  formLoading = false;
  form1: UntypedFormGroup;

  get CATEGORIES()       { return this.activityService.categories; }
  get LEAD_TIMES()       { return this.activityService.leadTimes; }

  readonly BOOKING_METHODS = [
    { value: 'Phone call',                 name: 'Phone call' },
    { value: 'Online booking link',        name: 'Online booking link' },
    { value: 'Email request',              name: 'Email request' },
    { value: 'KBM concierge arranges',     name: 'KBM concierge arranges' },
    { value: 'Walk-in',                    name: 'Walk-in' },
  ];

  readonly CANCEL_POLICIES = [
    { value: '24-hour notice required',    name: '24-hour notice required' },
    { value: '48-hour notice required',    name: '48-hour notice required' },
    { value: '72-hour notice required',    name: '72-hour notice required' },
    { value: 'Non-refundable',             name: 'Non-refundable' },
    { value: 'Flexible — full refund',     name: 'Flexible — full refund' },
  ];

  readonly STATUSES = [
    { value: '0', name: 'Active' },
    { value: '2', name: 'Suspended' },
    { value: '1', name: 'Cancelled' },
  ];

  constructor(
    private fb: UntypedFormBuilder,
    private activityProviderService: ActivityProviderService,
    private activityService: ActivityService,
    private alertService: AlertService,
    @Inject(MAT_DIALOG_DATA) data: any,
  ) {
    this.formTitle        = data.title;
    this.activityProvider = data.activityProvider;
  }

  ngOnInit(): void {
    this.isNew = !this.activityProvider?.activityProviderId;
    this.buildForm();
    if (this.activityProvider) {
      this.form1.patchValue(this.activityProvider);
    }
  }

  buildForm() {
    this.form1 = this.fb.group({
      activityProviderId: [this.activityProvider?.activityProviderId],
      // Basic
      name:             ['', [Validators.required, Validators.minLength(3)]],
      category:         [''],
      website:          [''],
      status:           ['0'],
      // Contact
      contactName:      ['', Validators.required],
      contactRole:      [''],
      phone:            ['', Validators.required],
      phone2:           [''],
      email:            [''],
      bestTimeToCall:   [''],
      // Booking
      bookingMethod:    ['Phone call'],
      leadTime:         ['No minimum'],
      bookingUrl:       [''],
      groupRate:        [''],
      staffNotes:       ['', Validators.required],
      guestInstructions:[''],
      // Cancellation
      cancellationPolicy: ['24-hour notice required'],
      paymentMethods:   [''],
      additionalNotes:  [''],
      // Legacy
      billingType:      [''],
      publicDesc:       [''],
      opsNotes:         [''],
      contractDate:     [''],
    });
  }

  save() {
    this.form1.markAllAsTouched();
    if (this.form1.invalid) return;

    this.formLoading = true;
    const obs = this.isNew
      ? this.activityProviderService.add(this.form1.value)
      : this.activityProviderService.update(this.form1.value);

    obs.pipe(first()).subscribe(
      (res: any) => {
        const saved = res.data.activityProvider;
        if (this.isNew) this.doAdd.emit(saved);
        else             this.doUpdate.emit(saved);
        this.formLoading = false;
      },
      (err: any) => {
        this.alertService.error(err);
        this.formLoading = false;
      }
    );
  }

  onDelete() {
    if (!this.activityProvider?.activityProviderId) return;
    this.activityProviderService.delete(this.activityProvider.activityProviderId).pipe(first()).subscribe(
      () => this.doDelete.emit(this.activityProvider),
      (err: any) => this.alertService.error(err)
    );
  }

  errMsg(field: string): string | null {
    const c = this.form1.get(field);
    if (!c || !c.touched || c.valid) return null;
    if (c.hasError('required'))  return 'Required';
    if (c.hasError('minlength')) return 'Too short';
    return 'Invalid';
  }
}
